import * as DateFnsNs from 'date-fns'
import { ptBR } from 'date-fns/locale/pt-BR'
import { ExternalAuthConfig } from 'src/auth.types'
import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { NotificationManagerService } from 'src/modules/admin/configurations/notification-manager.service'
import { NotificationTypesManagerService } from 'src/modules/admin/configurations/notification-types-manager.service'
import { AppStorage, lodash, ServiceError, TelemetryService } from 'src/utils'
import {
    AlertSeverity,
    ApplicationPresenter,
    FlipIntent,
    HistoryManager,
    Logger,
    NOOP_VOID,
    Presenter,
    Scope
} from 'wdc-cube'
import { Places } from '../Constants'
import { MainKeys } from './Main.keys'
import {
    AlertScope,
    BodyScope,
    ChangePasswordScope,
    IDialogScope,
    MainScope,
    ResetPasswordScope,
    SignInScope
} from './Main.scopes'
import { MainService } from './Main.service'
import { ReleaseNotesService } from './ReleaseNotes.service'

const LOG = Logger.get('MainPresenter')

const appStorage = AppStorage.singleton()
const mainService = MainService.singleton()
const notificationService = NotificationManagerService.singleton()
const notificationTypeService = NotificationTypesManagerService.singleton()
const releaseNotesService = ReleaseNotesService.singleton()
const telemetryService = TelemetryService.singleton()

export class MainPresenter extends ApplicationPresenter<MainScope> {
    // :: Instance

    private __removeAccessTokenAboutToExpireListener = NOOP_VOID

    private readonly __signInScope = new SignInScope()
    private readonly __resetPasswordScope = new ResetPasswordScope()
    private readonly __changePasswordScope = new ChangePasswordScope()
    private readonly __defaultScope = new BodyScope()

    private readonly __bodySlot = this.__setBodySlot.bind(this)
    private readonly __dialogSlot = this.showDialog.bind(this)

    private __resetPassword = false
    private __changePassword = false
    private __changePasswordToken?: string
    private __compliance = false
    private __defaultPlace = Places.overview

    constructor(historyManager: HistoryManager) {
        super(historyManager, new MainScope())

        // Important to allow newUriFromString to work properly
        this.setPlaces(Places)
    }

    // :: Properties

    get compliance() {
        return this.__compliance
    }

    get rdStationEnabled() {
        return this.scope.rdStationEnabled
    }

    get language() {
        return 'pt-BR'
    }

    // :: API

    initialize() {
        let initialized = false

        const bootstrap = async () => {
            try {
                await this.kickStart(Places.main)
                initialized = true
            } catch (error) {
                LOG.error('Initializing', error)
                this.release()
            }

            if (initialized && this.lastPlace === Places.main && this.scope.authenticated) {
                try {
                    this.flip(this.__defaultPlace)
                } catch (error) {
                    LOG.error('Flipping to Overview on page initialization', error)
                }
            }
        }

        bootstrap().catch(LOG.caught)

        return () => {
            if (initialized) {
                this.release()
            }
        }
    }

    override release() {
        this.__removeAccessTokenAboutToExpireListener()
        this.__removeAccessTokenAboutToExpireListener = NOOP_VOID
        super.release()
    }

    override async applyParameters(intent: FlipIntent, initialization: boolean, last?: boolean): Promise<boolean> {
        const keys = new MainKeys(this, intent)

        if (initialization) {
            await this.__intializeState(keys)
        } else {
            this.__compliance = keys.compliance
        }
        this.scope.activePage = intent.place.pathName

        if (this.lastPlace !== keys.targetPlace) {
            this.scope.toolbar = undefined
        }

        if (
            !initialization &&
            last &&
            intent.place === Places.main &&
            this.scope.authenticated &&
            !this.__resetPassword &&
            !this.__changePassword
        ) {
            await this.flip(this.__defaultPlace)
            return true
        }

        if (
            this.scope.authenticated &&
            keys.targetPlace.path.some(
                (place) =>
                    (place === Places.accessAudit && !this.scope.accessAuditEnabled) ||
                    (place === Places.billing && !this.scope.billingEnabled)
            )
        ) {
            await this.flip(this.__defaultPlace)
            return true
        }

        if (keys.microsoftLoginCode) {
            const success = await this._handleMicrosoftSignIn(keys.microsoftLoginCode)
            if (success) {
                return true
            }
        }

        if (keys.googleLoginCode) {
            const success = await this._handleGoogleSignIn(keys.googleLoginCode)
            if (success) {
                return true
            }
        }

        if (last || !this.scope.authenticated || this.__resetPassword || this.__changePassword) {
            this.scope.toolbar = undefined

            this.__bodySlot(undefined)
            keys.allow = false
        } else {
            keys.parentSlot = this.__bodySlot
            keys.dialogSlot = this.__dialogSlot
            this.__trackPageView(intent)
        }

        if (keys.last) {
            keys.dialogSlot(null)
        }

        return keys.allow
    }

    private __trackPageView(intent: FlipIntent) {
        const place = intent.place

        if (place === Places.main || place.id === -1 || this.getPresenter(place)) {
            return
        }

        telemetryService.pageView(place)
    }

    override publishParameters(intent: FlipIntent) {
        const keys = new MainKeys(this, intent)
        keys.resetPassword = this.__resetPassword
        keys.changePassword = this.__changePassword
        keys.changePasswordToken = this.__changePasswordToken
        keys.compliance = this.__compliance
    }

    private async __intializeState(keys: MainKeys) {
        this.__resetPassword = keys.resetPassword
        this.__changePassword = keys.changePassword
        this.__compliance = keys.compliance
        this.__changePasswordToken = keys.changePasswordToken

        this.__removeAccessTokenAboutToExpireListener = appStorage.onAccessTokenAboutToExpire(
            this.__handleAccessTokenAboutToExpire.bind(this)
        )

        this.scope.onSignOut = this.action(this._handleSignOut)
        this.scope.onProfile = this.action(this._handleProfile)
        this.scope.onToggleComplianceMode = this.action(this._handleToggleComplianceMode)
        this.scope.onDrawerToggle = this.action(this._handleDrawerToggle)
        this.scope.onHome = this.action(this._handleHome)
        this.scope.onOverview = this.action(this._handleOverview)
        this.scope.onTheConnection = this.action(this._handleTheConnection)
        this.scope.onTheMap = this.action(this._handleTheMap)
        this.scope.onTheActing = this.action(this._handleTheActing)
        this.scope.onTheConsumerMarket = this.action(this._handleTheConsumerMarket)
        this.scope.onMercadoVet = this.action(this._handleMercadoVet)
        this.scope.onAnaliseDesempenho = this.action(this._handleAnaliseDesempenho)
        this.scope.onRdStation = this.action(this._handleRdStation)
        this.scope.onUserManager = this.action(this._handleUserManager)
        this.scope.onGroupManager = this.action(this._handleGroupManager)
        this.scope.onConfigManager = this.action(this._handleConfigManager)
        this.scope.onAccessAudit = this.action(this._handleAccessAudit)
        this.scope.onBilling = this.action(this._handleBilling)
        this.scope.onMcPj = this.action(this._handleMcPj)
        this.scope.onMcPf = this.action(this._handleMcPf)
        this.scope.onMcSerasa = this.action(this._handleMcSerasa)
        this.scope.onMcSerasaHistory = this.action(this._handleMcSerasaHistory)
        this.scope.onMcSerpro = this.action(this._handleMcSerpro)
        this.scope.onMcGrupo = this.action(this._handleMcGrupo)
        this.scope.onMcAnalise = this.action(this._handleMcAnalise)
        this.scope.onMcBordero = this.action(this._handleMcBordero)
        this.scope.onMcMonitoramento = this.action(this._handleMcMonitoramento)

        this.scope.notificationsScope.onNotificationTabChange = this.action(this._handleNotificationTabChange)
        this.scope.notificationsScope.onMarkNotificationAsRead = this.action(this._handleMarkNotificationAsRead)
        this.scope.notificationsScope.onMarkAllNotificationAsRead = this.action(this._handleMarkAllNotificationAsRead)
        this.scope.notificationsScope.onReadChange = this.action(this._handleNotificationsReadChange)
        this.scope.notificationsScope.onShowExpiredChange = this.action(this._handleNotificationsShowExpiredChange)
        this.scope.notificationsScope.onNotificationDrawerToggle = this.action(this._handleNotificationDrawerToggle)
        this.scope.notificationsScope.onAlertModalClose = this.action(this._handleAlertModalClose)

        this.scope.releaseNotesScope.onOpenReleaseNotes = this.action(this._handleOpenReleaseNotes)

        this.__signInScope.rememberMe = appStorage.rememberMe
        this.__signInScope.onSignIn = this.action(this._handleSignIn)
        this.__signInScope.onForgotPassword = this.action(this._handleForgotPassword)
        this.__signInScope.onChangeRememberMe = this.action(this._handleChangeRememberMe)
        this.__signInScope.update = this.update

        await this._loadExternalAuthConfig()

        this.__resetPasswordScope.onResetPassword = this.action(this._handleResetPassword)
        this.__resetPasswordScope.onBackToLogin = this.action(this._handleSignOut)
        this.__resetPasswordScope.update = this.update

        this.__changePasswordScope.onChangePassword = this.action(this._handleChangePassword)
        this.__changePasswordScope.onBackToLogin = this.action(this._handleSignOut)

        this.__defaultScope.update = this.update

        this.scope.authenticated = appStorage.accessTokenExpiration >= Date.now()
        if (this.scope.authenticated) {
            this.scope.authenticated = await mainService.reloadAuthorizations()
        }

        this.scope.rdStationEnabled = this.scope.authenticated && appStorage.rdStationEnbled
        this.scope.theMapEnabled = this.scope.authenticated && appStorage.theMapEnabled
        this.scope.theActingEnabled = this.scope.authenticated && appStorage.theActingEnabled
        this.scope.theConsumerMarketEnabled = this.scope.authenticated && appStorage.theConsumerMarketEnabled
        this.scope.theConnectionEnabled = this.scope.authenticated && appStorage.theConnectionEnabled
        this.scope.userManagerEnabled = this.scope.authenticated && appStorage.userManagerEnabled
        this.scope.accessAuditEnabled = this.scope.authenticated && appStorage.accessAuditEnabled
        this.scope.billingEnabled = this.scope.authenticated && appStorage.billingEnabled
        this.scope.mercadoVetEnabled = this.scope.authenticated && appStorage.mercadoVetEnabled
        this.scope.analiseDesempenhoEnabled = this.scope.authenticated && appStorage.analiseDesempenhoEnabled
        this.__applyMotorCreditoAuthorizations(this.scope.authenticated)

        if (this.scope.authenticated && this.scope.notificationsScope.notificationTabs.length === 1) {
            await this.#loadNotificationsTypes()
        }

        if (this.scope.authenticated && !this.scope.notificationsScope.notifications.length) {
            await this.#loadNotifications()
        }

        if (this.scope.authenticated) {
            this.#loadReleaseNotesStatus().catch(LOG.caught)
        }
        LOG.info(`Initialized(${keys.toString()})`)
    }

    // :: Helper API

    override unexpected(message: string, error: unknown) {
        super.unexpected(message, error)
        if (error instanceof ServiceError) {
            this.alert('error', 'Erro proveniente do servidor', `${error.statusCode}: ${error.message}`)
        } else {
            this.alert('error', 'Erro não esperado', message)
        }
    }

    override alert(severity: AlertSeverity, title: string, message: string, onClose?: () => Promise<void>) {
        const alertScope = new AlertScope()
        alertScope.severity = severity
        alertScope.title = title
        alertScope.message = message
        alertScope.onClose = this.action(this._handleCloseAlert.bind(this, onClose))
        alertScope.update = this.update
        this.scope.alert = alertScope
    }

    notify(severity: AlertSeverity, message: string, duration = 3000) {
        this.scope.notify(severity, message, duration)
    }

    releaseToolbar(toolbar: Presenter<Scope>) {
        if (this.scope.toolbar === toolbar.scope) {
            this.scope.toolbar = undefined
        }
        toolbar.release()
    }

    formatDate(date: Date) {
        return DateFnsNs.format(date, 'dd/MM/yyyy', { locale: ptBR })
    }

    // :: View Actions

    private __setBodySlot(scope: Scope | undefined | null) {
        let newScope = scope
        if (this.__resetPassword) {
            newScope = this.__resetPasswordScope
        } else if (this.__changePassword) {
            newScope = this.__changePasswordScope
        } else if (this.scope.authenticated) {
            newScope = scope ?? this.__defaultScope
        } else {
            newScope = this.__signInScope
        }

        this.scope.body = newScope
    }

    public showDialog(scope: Scope | undefined | null) {
        if (this.scope.dialog !== scope) {
            this.scope.dialog = scope as IDialogScope

            if (this.scope.dialog && !this.scope.dialog.onClose) {
                LOG.error(`Missing onClose action on scope ${this.scope.dialog.constructor.name}`)
            }
        }
    }

    private __handleAccessTokenAboutToExpire() {
        LOG.debug('handleAccessTokenAboutToExpire')
        const action = async () => {
            try {
                await mainService.refreshAccessToken(this.__signInScope.rememberMe ?? false)
            } catch (err) {
                this.action(this._handleSignOut)()
            }
        }
        action().catch(LOG.caught)
    }

    protected async _handleCloseAlert(onClose?: () => Promise<void>) {
        this.scope.alert = undefined
        if (onClose) {
            await onClose()
        }
    }

    protected async _handleLoginSuccess() {
        this.scope.authenticated = true
        this.scope.rdStationEnabled = appStorage.rdStationEnbled
        this.scope.theMapEnabled = appStorage.theMapEnabled
        this.scope.theActingEnabled = appStorage.theActingEnabled
        this.scope.theConsumerMarketEnabled = appStorage.theConsumerMarketEnabled
        this.scope.theConnectionEnabled = appStorage.theConnectionEnabled
        this.scope.userManagerEnabled = appStorage.userManagerEnabled
        this.scope.accessAuditEnabled = appStorage.accessAuditEnabled
        this.scope.billingEnabled = appStorage.billingEnabled
        this.scope.mercadoVetEnabled = appStorage.mercadoVetEnabled
        this.scope.analiseDesempenhoEnabled = appStorage.analiseDesempenhoEnabled
        this.__applyMotorCreditoAuthorizations(true)

        this.__computeDefaultPlace()

        // Após o login sempre abre a página inicial, ignorando a URL que estava aberta antes de autenticar
        await this.flipToDefault()
    }

    protected async _handleMicrosoftSignIn(code: string) {
        if (!code) {
            this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
            return false
        }

        try {
            await mainService.microsoftSignIn(code)

            await this._handleLoginSuccess()
            return true
        } catch (err) {
            if (err instanceof ServiceError) {
                if (err.statusCode === 401) {
                    if (err.message === 'User is inactive') {
                        this.__signInScope.errorMessage = 'Usuário inativo, contate o administrador do sistema'
                        return false
                    }
                    if (err.message === 'User is deleted') {
                        this.__signInScope.errorMessage = 'Usuário removido, contate o administrador do sistema'
                        return false
                    }
                    this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
                    return false
                }
            }
            throw err
        }
    }

    protected async _handleGoogleSignIn(code: string) {
        if (!code) {
            this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
            return false
        }

        try {
            await mainService.googleSignIn(code)

            this._handleLoginSuccess()
            return true
        } catch (err) {
            if (err instanceof ServiceError) {
                if (err.statusCode === 401) {
                    if (err.message === 'User is inactive') {
                        this.__signInScope.errorMessage = 'Usuário inativo, contate o administrador do sistema'
                        return false
                    }
                    if (err.message === 'User is deleted') {
                        this.__signInScope.errorMessage = 'Usuário removido, contate o administrador do sistema'
                        return false
                    }
                    this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
                    return false
                }
            }
            throw err
        }
    }

    protected async _loadExternalAuthConfig() {
        const resp = await fetch('/api/auth/external-config')
        if (resp.status !== 200) {
            return
        }
        const externalAuthConfig = (await resp.json()) as ExternalAuthConfig
        this.__signInScope.externalAuthConfig = externalAuthConfig
    }

    protected async _handleSignIn() {
        LOG.debug('handleSignIn')

        let hasError = false
        this.__signInScope.userNameError = undefined
        this.__signInScope.passwordError = undefined
        this.__signInScope.errorMessage = undefined

        const userName = this.__signInScope.userName
        if (!userName) {
            this.__signInScope.userNameError = 'Campo requirido'
            hasError = true
        }

        const password = this.__signInScope.password
        if (!password) {
            this.__signInScope.passwordError = 'Campo requirido'
            hasError = true
        }

        if (hasError) {
            return
        }

        try {
            await mainService.signIn(userName!, password!, this.__signInScope.rememberMe ?? true)

            this._handleLoginSuccess()
        } catch (err) {
            if (err instanceof ServiceError) {
                if (err.statusCode === 401) {
                    if (err.message === 'User is inactive') {
                        this.__signInScope.errorMessage = 'Usuário inativo, contate o administrador do sistema'
                        return
                    }
                    if (err.message === 'User is deleted') {
                        this.__signInScope.errorMessage = 'Usuário removido, contate o administrador do sistema'
                        return
                    }
                    this.__signInScope.errorMessage = 'Usuário desconhecido ou senha incorreta'
                    return
                }
            }
            throw err
        }
    }

    private __applyMotorCreditoAuthorizations(authenticated: boolean) {
        this.scope.motorCreditoEnabled = authenticated && appStorage.motorCreditoEnabled
        this.scope.mcPjEnabled = authenticated && appStorage.mcPjEnabled
        this.scope.mcPfEnabled = authenticated && appStorage.mcPfEnabled
        this.scope.mcSerasaEnabled = authenticated && appStorage.mcSerasaEnabled
        this.scope.mcSerasaHistoryEnabled = authenticated && appStorage.mcSerasaHistoryEnabled
        this.scope.mcSerproEnabled = authenticated && appStorage.mcSerproEnabled
        this.scope.mcGrupoEnabled = authenticated && appStorage.mcGrupoEnabled
        this.scope.mcAnaliseEnabled = authenticated && appStorage.mcAnaliseEnabled
        this.scope.mcBorderoEnabled = authenticated && appStorage.mcBorderoEnabled
        this.scope.mcMonitoramentoEnabled = authenticated && appStorage.mcMonitoramentoEnabled
    }

    private __computeDefaultPlace() {
        this.__defaultPlace = Places.overview
    }

    protected async _handleSignOut() {
        LOG.debug('handleSignOut')
        this.scope.authenticated = false
        this.__resetPassword = false
        this.__changePassword = false
        this.__changePasswordToken = undefined
        this.__signInScope.rememberMe = false
        this.__signInScope.userName = undefined
        this.__signInScope.userNameError = undefined
        this.__signInScope.password = undefined
        this.__signInScope.passwordError = undefined
        this.__signInScope.errorMessage = undefined
        appStorage.clearAccessToken()
        await this.flip(this.rootPlace)
    }

    protected async _handleProfile() {
        await this.flip(Places.profile)
    }

    protected async _handleToggleComplianceMode() {
        this.__compliance = !this.__compliance
        this.updateHistory()

        this.forEachPresenter((child) => {
            const childAsRecord = child as unknown as Record<string, unknown>
            const onComplianceChangedListener = childAsRecord['onComplianceChanged']
            if (lodash.isFunction(onComplianceChangedListener)) {
                onComplianceChangedListener.call(child, this.__compliance)
            }
        })
    }

    public async flipToDefault() {
        await this.flip(this.__defaultPlace)
    }

    protected async _handleChangeRememberMe(rememberMe: boolean) {
        appStorage.rememberMe = rememberMe
        this.__signInScope.rememberMe = rememberMe
    }

    protected async _handleForgotPassword() {
        LOG.info('handleForgotPassword')
        this.__resetPassword = true
        this.__resetPasswordScope.email = undefined
        await this.flip(this.rootPlace)
    }

    protected async _handleResetPassword() {
        LOG.info('handleResetPassword')
        this.__resetPasswordScope.emailError = undefined
        if (this.__resetPasswordScope.email) {
            await mainService.requestPasswordReset(this.__resetPasswordScope.email)
            this.scope.notify('success', 'Solicitação enviada', 3000)
        } else {
            this.__resetPasswordScope.emailError = 'Campo requerido'
            return
        }

        this.__resetPassword = false
        await this._handleSignOut()
    }

    protected async _handleChangePassword() {
        LOG.info('handleChangePassword')
        this.__changePasswordScope.passwordError = undefined

        if (!this.__changePasswordToken) {
            this.__changePasswordScope.passwordError = 'Token de redefinição não informado'
            return
        }
        if (!this.__changePasswordScope.password) {
            this.__changePasswordScope.passwordError = 'Senha deve ser informada'
            return
        }
        if (!this.__changePasswordScope.passwordConfirmation) {
            this.__changePasswordScope.passwordError = 'Confirmação de senha deve ser informada'
            return
        }

        if (this.__changePasswordScope.password !== this.__changePasswordScope.passwordConfirmation) {
            this.__changePasswordScope.passwordError = 'Senha e Confirmação de senha devem ser iguais'
            return
        }

        try {
            this.__changePasswordScope.waiting = new WaitingScope()

            await mainService.changePassword(
                this.__changePasswordScope.password!,
                this.__changePasswordScope.passwordConfirmation!,
                this.__changePasswordToken!
            )
            this.scope.notify('success', 'Senha alterada com sucesso', 3000)
            this.__changePassword = false
            await this._handleSignOut()
        } catch (error) {
            const err = error as { message: string }
            this.__changePasswordScope.passwordError = err.message
        } finally {
            this.__changePasswordScope.waiting = undefined
        }
    }

    protected async _handleDrawerToggle() {
        this.scope.drawerOpen = !this.scope.drawerOpen
    }

    protected async _handleNotificationDrawerToggle() {
        this.scope.notificationsScope.notificationDrawerOpen = !this.scope.notificationsScope.notificationDrawerOpen
    }

    protected async _handleAlertModalClose() {
        this.scope.notificationsScope.alertModalOpen = false

        const dispensaveis = this.scope.notificationsScope.alerts.filter((n) => n.canMarkAsRead && !n.readAt)
        if (dispensaveis.length === 0) {
            return
        }

        await notificationService.markAsRead(dispensaveis.map((n) => n.id))
        this.#loadNotifications().catch(LOG.caught)
    }

    protected async _handleHome() {
        await this.flip(this.rootPlace)
    }

    protected async _handleOverview() {
        await this.flip(Places.overview)
    }

    protected async _handleTheConnection() {
        await this.flip(Places.theConnection)
    }

    protected async _handleTheMap() {
        await this.flip(Places.theMap)
    }

    protected async _handleTheActing() {
        await this.flip(Places.theActing)
    }

    protected async _handleTheConsumerMarket() {
        await this.flip(Places.theConsumerMarket)
    }

    protected async _handleMercadoVet() {
        await this.flip(Places.mercadoVet)
    }

    protected async _handleAnaliseDesempenho() {
        await this.flip(Places.analiseDesempenho)
    }

    protected async _handleRdStation() {
        await this.flip(Places.rdStation)
    }

    protected async _handleUserManager() {
        await this.flip(Places.userManager)
    }

    protected async _handleGroupManager() {
        await this.flip(Places.groupManager)
    }

    protected async _handleConfigManager() {
        await this.flip(Places.configurations)
    }

    protected async _handleAccessAudit() {
        await this.flip(Places.accessAudit)
    }

    // :: Motor de Crédito

    protected async _handleMcPj() {
        await this.flip(Places.mcPj)
    }

    protected async _handleMcPf() {
        await this.flip(Places.mcPf)
    }

    protected async _handleMcSerasa() {
        await this.flip(Places.mcSerasa)
    }

    protected async _handleMcSerasaHistory() {
        await this.flip(Places.mcSerasaHistory)
    }

    protected async _handleMcSerpro() {
        await this.flip(Places.mcSerpro)
    }

    protected async _handleMcGrupo() {
        await this.flip(Places.mcGrupo)
    }

    protected async _handleMcAnalise() {
        await this.flip(Places.mcAnalise)
    }

    protected async _handleMcBordero() {
        await this.flip(Places.mcBordero)
    }

    protected async _handleMcMonitoramento() {
        await this.flip(Places.mcIntegracaoMonitoramento)
    }

    protected async _handleBilling() {
        await this.flip(Places.billing)
    }

    protected async _handleNotificationsReadChange(showRead: boolean) {
        this.scope.notificationsScope.showRead = showRead
        this.#loadNotifications().catch(LOG.caught)
    }

    protected async _handleNotificationsShowExpiredChange(showExpired: boolean) {
        this.scope.notificationsScope.showExpired = showExpired
        this.#loadNotifications().catch(LOG.caught)
    }

    protected async _handleNotificationTabChange(tab: string) {
        this.scope.notificationsScope.notificationTab = tab
        this._filterNotificationsByTab()
    }

    protected async _handleMarkNotificationAsRead(id: number) {
        await notificationService.markAsRead([id])
        this.#loadNotifications().catch(LOG.caught)
    }

    protected async _handleMarkAllNotificationAsRead() {
        this.scope.notificationsScope.waiting = new WaitingScope()
        const unreadMessages = this.scope.notificationsScope.notifications.filter((n) => n.canMarkAsRead && !n.readAt)

        await notificationService.markAsRead(unreadMessages.map((m) => m.id))
        this.#loadNotifications().catch(LOG.caught)
    }

    protected _filterNotificationsByTab() {
        const { notifications, notificationTab } = this.scope.notificationsScope
        const filteredNotifications = notifications.filter(
            (n) => 'Todas' === notificationTab || n.type === notificationTab
        )
        this.scope.notificationsScope.filteredNotifications = filteredNotifications
    }

    async #loadNotificationsTypes() {
        const notTypes = await notificationTypeService.fetchAll(true)
        this.scope.notificationsScope.notificationTabs = ['Todas', ...notTypes.map((n) => n.name)]
    }

    async #loadNotifications() {
        try {
            this.scope.notificationsScope.waiting = new WaitingScope()
            const notifications = await notificationService.fetchMyNotifications(
                this.scope.notificationsScope.showRead,
                this.scope.notificationsScope.showExpired
            )
            this.scope.notificationsScope.notifications = notifications

            const unreadMessages = notifications.filter((n) => !n.expired && !n.readAt)

            this.scope.notificationsScope.notificationTabs.forEach((tab) => {
                const typeMessagesCount = unreadMessages.filter((n) => tab === 'Todas' || n.type === tab).length
                this.scope.notificationsScope.unreadMessagesByType = {
                    ...this.scope.notificationsScope.unreadMessagesByType,
                    [tab]: typeMessagesCount
                }
            })

            this.scope.notificationsScope.alerts = unreadMessages.filter((n) => n.alert)
            this.scope.notificationsScope.alertModalOpen = !!this.scope.notificationsScope.alerts.length

            this._filterNotificationsByTab()
        } catch (error) {
            LOG.error('Error loading notifications', error)
        } finally {
            this.scope.notificationsScope.waiting = undefined
            this.scope.forceUpdate()
        }
    }

    protected async _handleOpenReleaseNotes() {
        const releaseNotesScope = this.scope.releaseNotesScope

        if (!releaseNotesScope.pageUrl) {
            await this.#loadReleaseNotesStatus()
        }

        const pageUrl = releaseNotesScope.pageUrl
        if (pageUrl) {
            window.open(pageUrl, '_blank', 'noopener,noreferrer')
        } else {
            LOG.error('Nao foi possivel obter a url das novidades')
        }

        if (releaseNotesScope.unreadCount <= 0) {
            return
        }

        releaseNotesScope.unreadCount = 0
        try {
            await releaseNotesService.markAsRead()
        } catch (error) {
            LOG.error('Error marking release notes as read', error)
            this.#loadReleaseNotesStatus().catch(LOG.caught)
        }
    }

    async #loadReleaseNotesStatus() {
        try {
            const status = await releaseNotesService.fetchStatus()
            this.scope.releaseNotesScope.unreadCount = status.unreadCount
            this.scope.releaseNotesScope.pageUrl = status.pageUrl
        } catch (error) {
            LOG.error('Error loading release notes status', error)
        } finally {
            this.scope.forceUpdate()
        }
    }
}
